import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaTag } from '@react-icons/all-files/fa/FaTag';
import { FaBuilding } from '@react-icons/all-files/fa/FaBuilding';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import Breadcrumb from '../../../commons/Breadcrumb';
import PageHeader from '../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../commons/PageAlert';
import Confirm, { useConfirm } from '../../../commons/Confirm';
import { EditButton, DeleteButton, RenderActions } from '../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../commons/Datagrid';
import BooleanCell from '../../../commons/BooleanCell';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getEntities, deleteEntity, reset } from '../../../manager/etiqueta.reducer';
import { EMPRESA_CONFIG_BASE, etiquetaEditPath, etiquetaNewPath } from './empresa-config-routes';
import { ETIQUETA_NIVELES, ETIQUETA_NIVEL_LABELS, etiquetaNivelLabel } from './etiqueta-form';

function ColorCell({ color }: { color?: string | null }) {
  if (!color) {
    return <span style={{ color: '#94a3b8' }}>—</span>;
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: 4,
          background: color,
          border: '1px solid #e2e8f0',
          flexShrink: 0,
        }}
      />
      <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>{color}</span>
    </span>
  );
}

export default function EtiquetaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => s.etiqueta.entities);
  const totalItems = useAppSelector(s => s.etiqueta.totalItems);
  const loading = useAppSelector(s => s.etiqueta.loading);
  const updating = useAppSelector(s => s.etiqueta.updating);
  const updateSuccess = useAppSelector(s => s.etiqueta.updateSuccess);

  const [rows, setRows] = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) { showToast('success', msg); window.history.replaceState({}, ''); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      dismiss();
      if (lastParams) dispatch(getEntities(lastParams));
      showToast('success', 'Etiqueta eliminada correctamente.');
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setConfirmLoading(updating); }, [updating]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const renderActions = (params: any) => (
    <RenderActions>
      <EditButton onClick={() => navigate(etiquetaEditPath(params.row.id))} />
      <DeleteButton onClick={() => {
        ask(
          <>¿Confirmas que deseas eliminar <strong>{params.row.nombre}</strong>? Esta acción no se puede deshacer.</>,
          () => dispatch(deleteEntity(params.row.id)),
          { title: 'Eliminar etiqueta', confirmLabel: 'Eliminar', variant: 'danger' },
        );
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />
    </RenderActions>
  );

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.5 },
    { field: 'nombre', headerName: 'Nombre', flex: 1.4 },
    { field: 'descripcion', headerName: 'Descripción', flex: 2 },
    {
      field: 'nivel',
      headerName: 'Nivel',
      flex: 1.2,
      renderCell: (params: any) => etiquetaNivelLabel(params.row.nivel),
    },
    {
      field: 'color',
      headerName: 'Color',
      flex: 1,
      renderCell: (params: any) => <ColorCell color={params.row.color} />,
    },
    {
      field: 'habilitado',
      headerName: 'Habilitado',
      flex: 0.9,
      renderCell: (params: any) => <BooleanCell value={params.row.habilitado} showLabel />,
    },
    { field: '__actions', headerName: '', flex: 1, renderCell: renderActions },
  ];

  const filters = [
    { label: 'Nombre', name: 'nombre' },
    {
      label: 'Nivel',
      name: 'nivel',
      type: 'select',
      single: true,
      options: ETIQUETA_NIVELES.map(value => ({ value, label: ETIQUETA_NIVEL_LABELS[value] })),
    },
    {
      label: 'Habilitado',
      name: 'habilitado',
      type: 'select',
      single: true,
      options: [
        { value: 'true', label: 'Sí' },
        { value: 'false', label: 'No' },
      ],
    },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Empresa', to: EMPRESA_CONFIG_BASE, icon: <FaBuilding /> },
        { label: 'Etiquetas', icon: <FaTag /> },
      ]} />

      <PageHeader
        icon={<FaTag />}
        iconColor="#ec4899"
        title="Etiquetas"
        subtitle="Catálogo de etiquetas de empresa"
        action={{
          label: <><FaPlus style={{ marginRight: 6 }} />Nueva etiqueta</>,
          onClick: () => navigate(etiquetaNewPath()),
          color: '#ec4899',
        }}
      />

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
